import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
  DollarSign, 
  Calendar, 
  CheckCircle, 
  XCircle, 
  Clock, 
  TrendingUp, 
  Users, 
  AlertTriangle,
  Eye,
  Edit,
  Filter,
  Search,
  Download,
  RefreshCw,
  CreditCard,
  Banknote,
  Zap,
  CalendarClock
} from 'lucide-react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const ComprehensiveAgentDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('30');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Advanced client search state
  const [clientSearch, setClientSearch] = useState({
    name: '',
    email: '',
    phone: '',
    policyNumber: ''
  });
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [showClientSearch, setShowClientSearch] = useState(false);
  const [paymentLink, setPaymentLink] = useState('');

  // HCC/Scheduled Payments data
  const [hccData, setHccData] = useState({
    cleared: [],
    declined: [],
    scheduled: []
  });
  

  useEffect(() => {
    loadDashboardData();
    loadHCCData();
    generatePaymentLink();
  }, [dateRange, statusFilter]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/agent/dashboard?period=${dateRange}&status=${statusFilter}`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadHCCData = async () => {
    try {
      const response = await axios.get(`/api/agent/hcc-summary?period=${dateRange}`);
      setHccData(response.data);
    } catch (error) {
      console.error('Error loading HCC data:', error);
    }
  };
  

  const generatePaymentLink = () => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/payment/${user?.agentId}`;
    setPaymentLink(link);
  };

  const copyToClipboard = async (text, message = 'Link copied to clipboard!') => {
    try {
      await navigator.clipboard.writeText(text);
      alert(message);
    } catch (err) {
      console.error('Failed to copy: ', err);
      alert('Failed to copy link');
    }
  };


  const chargeNowScheduledPayment = async (transactionId, paymentId) => {
    try {
      const response = await axios.post(`/api/payments/charge-now/${transactionId}/${paymentId}`);
      if (response.data.success) {
        alert('Payment processing initiated');
        loadHCCData(); // Refresh data
      }
    } catch (error) {
      console.error('Error charging payment:', error);
      alert('Failed to process payment now');
    }
  };

  const reschedulePayment = async (transactionId, paymentId) => {
    const newDate = prompt('Enter new date (YYYY-MM-DD):');
    if (!newDate) return;
    
    try {
      const response = await axios.put(`/api/payments/scheduled-payment/${transactionId}/${paymentId}`, {
        date: newDate
      });
      if (response.data.success) {
        alert('Payment rescheduled successfully');
        loadHCCData(); // Refresh data
      }
    } catch (error) {
      console.error('Error rescheduling payment:', error);
      alert('Failed to reschedule payment');
    }
  };


  const searchClients = async () => {
    try {
      const searchParams = Object.entries(clientSearch)
        .filter(([key, value]) => value.trim() !== '')
        .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});

      if (Object.keys(searchParams).length === 0) {
        setClientSearchResults([]);
        return;
      }

      const response = await axios.post('/api/agent/search-clients', searchParams);
      setClientSearchResults(response.data.results || []);
    } catch (error) {
      console.error('Error searching clients:', error);
      setClientSearchResults([]);
    }
  };

  const clearClientSearch = () => {
    setClientSearch({ name: '', email: '', phone: '', policyNumber: '' });
    setClientSearchResults([]);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'cleared':
        return 'text-green-600 bg-green-100';
      case 'declined':
      case 'failed':
        return 'text-red-600 bg-red-100';
      case 'scheduled':
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'processing':
        return 'text-blue-600 bg-blue-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getPaymentMethodIcon = (method, isCompToComp = false) => {
    if (isCompToComp) {
      return <div className="flex items-center"><CreditCard size={16} className="text-purple-600 mr-1" /><span className="text-xs text-purple-600">COMP</span></div>;
    }
    if (method === 'cash') {
      return <Banknote size={16} className="text-green-600" />;
    }
    return <CreditCard size={16} className="text-gray-400" />;
  };

  const filteredHccData = (data) => {
    return data.filter(item => 
      searchTerm === '' || 
      item.client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.transactionId.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center">
          <RefreshCw className="animate-spin h-8 w-8 text-blue-600 mb-4" />
          <span className="text-gray-600">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 via-blue-700 to-red-600 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <div className="p-2 bg-white bg-opacity-20 rounded-lg mr-4">
                <Users className="h-8 w-8 text-white" />
              </div>
              <div>
                <div className="flex items-center space-x-4">
                  <h1 className="text-2xl font-bold text-white">
                    Agent Dashboard
                  </h1>
                  <div className="paylow-logo text-xl">
                    <span className="text-white font-bold">Pay</span><span className="text-red-200 font-bold">Low</span>
                  </div>
                </div>
                <p className="text-blue-100 mt-1">
                  {user?.firstName} {user?.lastName} - Agent ID: {user?.agentId}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => loadDashboardData()}
                className="p-2 text-white hover:text-gray-200 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
              >
                <RefreshCw size={20} />
              </button>
              <button
                onClick={logout}
                className="bg-red-500 bg-opacity-90 text-white px-4 py-2 rounded-lg hover:bg-opacity-100 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
          
          {/* Navigation Tabs */}
          <div className="border-t border-white border-opacity-20">
            <nav className="flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => window.location.href = '/agent'}
                className="border-b-2 border-white text-white py-4 px-1 text-center font-medium text-sm"
              >
                Dashboard
              </button>
              <button
                onClick={() => window.location.href = '/payment'}
                className="border-b-2 border-transparent text-blue-100 hover:text-white hover:border-white hover:border-opacity-50 py-4 px-1 text-center font-medium text-sm transition-colors"
              >
                Payment Processor
              </button>
            </nav>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Controls */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="form-select rounded-lg border-gray-300"
            >
              <option value="7">Last 7 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="90">Last 90 Days</option>
              <option value="365">Last Year</option>
            </select>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-select rounded-lg border-gray-300"
            >
              <option value="all">All Statuses</option>
              <option value="completed">Completed Only</option>
              <option value="pending">Pending Only</option>
              <option value="declined">Declined Only</option>
            </select>

            <div className="relative">
              <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search clients, emails, or transaction IDs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-80 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-green-50 to-white rounded-xl shadow-sm p-6 border border-green-100 hover:shadow-md transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {formatCurrency(stats.totalRevenue)}
                </p>
                <p className="text-sm text-green-600 mt-1">
                  +{stats.revenueGrowth || 0}% from last period
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl shadow-sm p-6 border border-blue-100 hover:shadow-md transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Transactions</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {stats.totalTransactions || 0}
                </p>
                <p className="text-sm text-blue-600 mt-1">
                  {stats.completedTransactions || 0} completed
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-white rounded-xl shadow-sm p-6 border border-purple-100 hover:shadow-md transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Clients</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {stats.activeClients || 0}
                </p>
                <p className="text-sm text-purple-600 mt-1">
                  {stats.newClients || 0} new this period
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl shadow-sm p-6 border border-orange-100 hover:shadow-md transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">HCCs Pending</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {hccData.scheduled?.length || 0}
                </p>
                <p className="text-sm text-orange-600 mt-1">
                  {formatCurrency(hccData.scheduled?.reduce((sum, hcc) => sum + (hcc.amount || 0), 0) || 0)} at risk
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>


        {/* Client Search Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Client Search</h3>
              <button
                onClick={() => setShowClientSearch(!showClientSearch)}
                className="text-blue-600 hover:text-blue-700 font-medium text-sm"
              >
                {showClientSearch ? 'Hide Search' : 'Show Search'}
              </button>
            </div>
          </div>
          
          {showClientSearch && (
            <div className="p-6">
              {/* Search Form */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
                  <input
                    type="text"
                    placeholder="Search by name..."
                    value={clientSearch.name}
                    onChange={(e) => setClientSearch(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    placeholder="Search by email..."
                    value={clientSearch.email}
                    onChange={(e) => setClientSearch(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    placeholder="Search by phone..."
                    value={clientSearch.phone}
                    onChange={(e) => setClientSearch(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Policy Number</label>
                  <input
                    type="text"
                    placeholder="Search by policy..."
                    value={clientSearch.policyNumber}
                    onChange={(e) => setClientSearch(prev => ({ ...prev, policyNumber: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              {/* Search Buttons */}
              <div className="flex space-x-3 mb-6">
                <button
                  onClick={searchClients}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                >
                  <Search size={16} className="mr-2" />
                  Search Clients
                </button>
                <button
                  onClick={clearClientSearch}
                  className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Clear
                </button>
              </div>
              
              {/* Search Results */}
              {clientSearchResults.length > 0 && (
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4">
                    Search Results ({clientSearchResults.length})
                  </h4>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {clientSearchResults.map((client, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4 border">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <button
                              onClick={() => navigate(`/client/${encodeURIComponent(client.email)}`)}
                              className="text-left"
                            >
                              <h5 className="font-semibold text-blue-600 hover:text-blue-800 underline cursor-pointer">{client.name}</h5>
                            </button>
                            <p className="text-sm text-gray-600">{client.email}</p>
                            {client.phone && <p className="text-sm text-gray-600">{client.phone}</p>}
                            {client.policyNumbers && client.policyNumbers.length > 0 && (
                              <p className="text-sm text-blue-600">
                                Policies: {client.policyNumbers.join(', ')}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-gray-900 font-semibold">
                              {formatCurrency(client.totalAmount)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {client.totalTransactions} transaction{client.totalTransactions !== 1 ? 's' : ''}
                            </div>
                            <div className="text-xs text-green-600">
                              Paid: {formatCurrency(client.paidAmount)}
                            </div>
                            {client.outstandingAmount > 0 && (
                              <div className="text-xs text-orange-600">
                                Outstanding: {formatCurrency(client.outstandingAmount)}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="mt-3 text-xs text-gray-500">
                          Last transaction: {new Date(client.lastTransaction).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {clientSearchResults.length === 0 && (
                Object.values(clientSearch).some(value => value.trim() !== '') && (
                  <div className="text-center py-8 text-gray-500">
                    <Search size={48} className="mx-auto mb-4 text-gray-300" />
                    <p>No clients found matching your search criteria.</p>
                  </div>
                )
              )}
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className="flex space-x-8">
            {[
              { key: 'overview', label: 'Overview', count: null },
              { key: 'cleared', label: 'Cleared HCCs', count: hccData.cleared?.length },
              { key: 'declined', label: 'Declined HCCs', count: hccData.declined?.length },
              { key: 'scheduled', label: 'Scheduled HCCs', count: hccData.scheduled?.length },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`pb-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.key
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-blue-600 hover:border-blue-300'
                }`}
              >
                {tab.label}
                {tab.count !== null && (
                  <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                    activeTab === tab.key 
                      ? 'bg-red-100 text-red-600' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Recent Activity */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-6 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900">Recent Transactions</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {dashboardData?.recentTransactions?.map((transaction) => (
                  <div key={transaction._id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div className="flex-1">
                            <button
                              onClick={() => navigate(`/client/${encodeURIComponent(transaction.client.email)}`)}
                              className="text-left"
                            >
                              <p className="text-sm font-medium text-blue-600 hover:text-blue-800 underline cursor-pointer">
                                {transaction.client.name}
                              </p>
                            </button>
                            <p className="text-sm text-gray-500">
                              {transaction.client.email}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-semibold text-gray-900">
                              {formatCurrency(transaction.amounts.total)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(transaction.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="ml-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(transaction.status)}`}>
                          {transaction.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* HCC Summary */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="p-6 border-b border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900">HCC Status Summary</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                    <span className="font-medium text-green-900">Cleared</span>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-green-900">
                      {hccData.cleared?.length || 0}
                    </p>
                    <p className="text-sm text-green-600">
                      {formatCurrency(hccData.cleared?.reduce((sum, hcc) => sum + (hcc.amount || 0), 0) || 0)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center">
                    <XCircle className="h-5 w-5 text-red-600 mr-3" />
                    <span className="font-medium text-red-900">Declined</span>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-red-900">
                      {hccData.declined?.length || 0}
                    </p>
                    <p className="text-sm text-red-600">
                      {formatCurrency(hccData.declined?.reduce((sum, hcc) => sum + (hcc.amount || 0), 0) || 0)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex items-center">
                    <Clock className="h-5 w-5 text-yellow-600 mr-3" />
                    <span className="font-medium text-yellow-900">Scheduled</span>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-yellow-900">
                      {hccData.scheduled?.length || 0}
                    </p>
                    <p className="text-sm text-yellow-600">
                      {formatCurrency(hccData.scheduled?.reduce((sum, hcc) => sum + (hcc.amount || 0), 0) || 0)}
                    </p>
                  </div>
                </div>
                
              </div>
            </div>
          </div>
        )}

        {/* Cleared HCCs Tab */}
        {activeTab === 'cleared' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Cleared HCCs - Successfully Collected
                </h3>
                <button className="flex items-center text-sm text-gray-600 hover:text-gray-900">
                  <Download size={16} className="mr-2" />
                  Export
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment Method
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Collected Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Transaction ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredHccData(hccData.cleared || []).map((hcc) => (
                    <tr key={hcc._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <button
                            onClick={() => navigate(`/client/${encodeURIComponent(hcc.client.email)}`)}
                            className="text-left"
                          >
                            <div className="text-sm font-medium text-blue-600 hover:text-blue-800 underline cursor-pointer">{hcc.client.name}</div>
                          </button>
                          <div className="text-sm text-gray-500">{hcc.client.email}</div>
                          {hcc.client.ghlContactId && (
                            <div className="text-xs text-blue-600">GHL: {hcc.client.ghlContactId}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {formatCurrency(hcc.amount)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getPaymentMethodIcon(hcc.paymentMethod, hcc.ccToComp)}
                          <span className="ml-2 text-sm text-gray-900 capitalize">
                            {hcc.ccToComp ? 'CC to COMP' : hcc.paymentMethod}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(hcc.collectedDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {hcc.transactionId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button className="text-blue-600 hover:text-blue-900 mr-4">
                          <Eye size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Declined HCCs Tab */}
        {activeTab === 'declined' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Declined HCCs - Collection Required
                </h3>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-red-600 font-medium">
                    Total at Risk: {formatCurrency(hccData.declined?.reduce((sum, hcc) => sum + (hcc.amount || 0), 0) || 0)}
                  </span>
                  <button className="flex items-center text-sm text-gray-600 hover:text-gray-900">
                    <Download size={16} className="mr-2" />
                    Export
                  </button>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Days Overdue
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Attempt
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Decline Reason
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredHccData(hccData.declined || []).map((hcc) => (
                    <tr key={hcc._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <button
                            onClick={() => navigate(`/client/${encodeURIComponent(hcc.client.email)}`)}
                            className="text-left"
                          >
                            <div className="text-sm font-medium text-blue-600 hover:text-blue-800 underline cursor-pointer">{hcc.client.name}</div>
                          </button>
                          <div className="text-sm text-gray-500">{hcc.client.email}</div>
                          {hcc.client.phone && (
                            <div className="text-sm text-gray-500">{hcc.client.phone}</div>
                          )}
                          {hcc.client.ghlContactId && (
                            <div className="text-xs text-blue-600">GHL: {hcc.client.ghlContactId}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-red-600">
                          {formatCurrency(hcc.amount)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`w-2 h-2 rounded-full mr-2 ${
                            hcc.daysOverdue > 30 ? 'bg-red-500' : 
                            hcc.daysOverdue > 14 ? 'bg-yellow-500' : 'bg-green-500'
                          }`}></div>
                          <span className="text-sm text-gray-900">{hcc.daysOverdue} days</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(hcc.lastAttemptDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-red-600 max-w-xs truncate" title={hcc.failureReason || hcc.declineReason || 'Unknown reason'}>
                          {hcc.failureReason || hcc.declineReason || 'Processing failed'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(hcc.collectionStatus)}`}>
                          {hcc.collectionStatus}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button 
                            onClick={() => chargeNowScheduledPayment(hcc.transactionId, hcc.paymentId)}
                            className="flex items-center px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                            title="Charge now"
                          >
                            <Zap size={12} className="mr-1" />
                            Charge
                          </button>
                          <button 
                            onClick={() => reschedulePayment(hcc.transactionId, hcc.paymentId)}
                            className="flex items-center px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
                            title="Reschedule payment"
                          >
                            <CalendarClock size={12} className="mr-1" />
                            Reschedule
                          </button>
                          <button 
                            onClick={() => navigate(`/client/${encodeURIComponent(hcc.client.email)}`)}
                            className="text-gray-600 hover:text-gray-900"
                            title="View client profile"
                          >
                            <Eye size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}


        {/* Scheduled HCCs Tab */}
        {activeTab === 'scheduled' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Scheduled HCCs - Upcoming Payments
                </h3>
                <button className="flex items-center text-sm text-gray-600 hover:text-gray-900">
                  <Download size={16} className="mr-2" />
                  Export
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Scheduled Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment Method
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Days Until Due
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredHccData(hccData.scheduled || []).map((hcc) => (
                    <tr key={hcc._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <button
                            onClick={() => navigate(`/client/${encodeURIComponent(hcc.client.email)}`)}
                            className="text-left"
                          >
                            <div className="text-sm font-medium text-blue-600 hover:text-blue-800 underline cursor-pointer">{hcc.client.name}</div>
                          </button>
                          <div className="text-sm text-gray-500">{hcc.client.email}</div>
                          {hcc.client.ghlContactId && (
                            <div className="text-xs text-blue-600">GHL: {hcc.client.ghlContactId}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {formatCurrency(hcc.amount)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(hcc.scheduledDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getPaymentMethodIcon(hcc.paymentMethod, hcc.ccToComp)}
                          <span className="ml-2 text-sm text-gray-900 capitalize">
                            {hcc.ccToComp ? 'CC to COMP' : hcc.paymentMethod}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`w-2 h-2 rounded-full mr-2 ${
                            hcc.daysUntilDue < 0 ? 'bg-red-500' : 
                            hcc.daysUntilDue <= 3 ? 'bg-yellow-500' : 'bg-green-500'
                          }`}></div>
                          <span className="text-sm text-gray-900">
                            {hcc.daysUntilDue < 0 ? `${Math.abs(hcc.daysUntilDue)} overdue` : `${hcc.daysUntilDue} days`}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button className="text-blue-600 hover:text-blue-900 mr-4">
                          <Edit size={16} />
                        </button>
                        <button className="text-gray-600 hover:text-gray-900">
                          <Eye size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComprehensiveAgentDashboard;